import React, { useState, useEffect } from 'react';
import { SignIn, SignUp } from '@clerk/clerk-react';
import {
  Smartphone, Zap, Brain, Bell, ArrowRight, Star,
  Menu, X, Activity, BarChart3
} from 'lucide-react';
import { useTranslation } from '../utils/translations';

const APP_STORE_URL = '';
const APP_STORE_READY = Boolean(APP_STORE_URL);
const STRIPE_URLS = {
  pro: {
    monthly: 'https://buy.stripe.com/3cI5kD2CvfvB3xm2ZTcAo01',
    quarterly: 'https://buy.stripe.com/28E7sL5OH8397NC0RLcAo03',
    yearly: 'https://buy.stripe.com/9B63cvb91bflec01VPcAo06',
  },
  premium: {
    monthly: 'https://buy.stripe.com/6oU4gz2Cv1EL2ti7g9cAo02',
    quarterly: 'https://buy.stripe.com/28E9AT1yrdnt1pe43XcAo04',
    yearly: 'https://buy.stripe.com/6oU7sLb913MTfg4dExcAo07',
  },
};

function AppleLogo({ className = 'w-5 h-5' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
    </svg>
  );
}

export default function LandingPage() {
  const { t } = useTranslation();
  const [showSignUp, setShowSignUp] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly');

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const whyApp = [
    { icon: Zap, title: 'Scanner 24/7', desc: 'Engine che analizza 50+ simboli ogni 15 minuti. Forex, oro, indici. Zero configurazione.', color: 'from-amber-500 to-orange-600' },
    { icon: Brain, title: 'AI integrata', desc: 'Multi-timeframe, checklist avanzata, validazione setup. Richiedi analisi con SL/TP suggeriti in un tap.', color: 'from-violet-500 to-purple-600' },
    { icon: Bell, title: 'Alert e monitoraggio', desc: 'Alert in tempo reale quando un setup supera soglia o quando TP/SL viene raggiunto.', color: 'from-cyan-500 to-blue-600' },
    { icon: Smartphone, title: 'Desktop e mobile', desc: 'Piattaforma multicanale: leaderboard, analisi e calcolatore di rischio su browser e app.', color: 'from-emerald-500 to-teal-600' },
  ];

  const howItWorksBlocks = [
    { n: '1', text: 'Lo scanner lavora da solo. Ogni 15 minuti controlla oltre 50 asset — coppie forex, oro, indici — e mette in fila i setup migliori: quelli con la più alta probabilità di andare in una direzione piuttosto che in un\'altra. Tu non devi fare nulla: apri l\'app o la piattaforma e vedi subito la classifica aggiornata.' },
    { n: '2', text: 'Scegli quello che ti interessa. Clicchi su un setup e vedi tutti i dettagli: come si è formato, su quali timeframe, che livello di accordo ha il mercato in quel momento. Se il setup è tra i più solidi, puoi chiedere all\'AI un\'analisi approfondita che ti dice dove potrebbe andare il prezzo, dove entrare e dove uscire.' },
    { n: '3', text: 'Operi con più chiarezza. L\'AI ti suggerisce stop loss e take profit, e tu puoi usare il calcolatore integrato per dimensionare la posizione. Quando sei in trade, ricevi avvisi in tempo reale. Niente più ore passate sui grafici: il lavoro pesante lo fa TradeAnalyzer.' },
  ];

  const billingOptions = [
    { key: 'monthly', label: 'Mensile', suffix: '/mese', pro: 29, premium: 59, proStrike: null, premiumStrike: null, badge: null },
    { key: 'quarterly', label: 'Trimestrale', suffix: '/3 mesi', pro: 79, premium: 169, proStrike: 87, premiumStrike: 177, badge: 'Risparmi 9%' },
    { key: 'yearly', label: 'Annuale', suffix: '/anno', pro: 279, premium: 599, proStrike: 348, premiumStrike: 708, badge: 'Best value' },
  ];

  const activeBilling = billingOptions.find((b) => b.key === billingCycle) || billingOptions[0];
  const stripeProUrl = STRIPE_URLS.pro?.[activeBilling.key] || STRIPE_URLS.pro.monthly;
  const stripePremiumUrl = STRIPE_URLS.premium?.[activeBilling.key] || STRIPE_URLS.premium.monthly;

  if (showAuth) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <button onClick={() => setShowAuth(false)} className="mb-8 text-slate-400 hover:text-white flex items-center gap-2 transition-colors">
            <ArrowRight className="w-4 h-4 rotate-180" />
            <span>Torna al sito</span>
          </button>
          <div className="mb-8 flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl overflow-hidden ring-1 ring-white/10 shadow-lg shrink-0">
              <img src="/logo.png" alt="" className="w-full h-full object-cover" width={48} height={48} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">TradeAnalyzer Pro</h1>
              <p className="text-slate-400 text-sm">{showSignUp ? 'Crea account' : 'Accedi'} alla piattaforma</p>
            </div>
          </div>
          <div className="clerk-auth-wrapper bg-slate-900/80 backdrop-blur-xl rounded-2xl p-1 border border-slate-700">
            {showSignUp ? (
              <SignUp appearance={{ elements: { rootBox: "w-full", card: "shadow-none bg-transparent" } }} />
            ) : (
              <SignIn appearance={{ elements: { rootBox: "w-full", card: "shadow-none bg-transparent" } }} />
            )}
          </div>
          <p className="mt-6 text-center text-slate-500 text-sm">
            {showSignUp ? 'Hai già un account?' : 'Non hai un account?'}{' '}
            <button onClick={() => setShowSignUp(!showSignUp)} className="text-violet-400 hover:text-violet-300 font-medium">
              {showSignUp ? 'Accedi' : 'Registrati'}
            </button>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-violet-500/20 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-cyan-500/15 rounded-full blur-[100px]" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.02)_1px,transparent_1px)] bg-[size:64px_64px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_0%,black,transparent)]" />
      </div>

      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-slate-950/90 backdrop-blur-xl border-b border-slate-800' : 'bg-transparent'}`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16 sm:h-18">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg overflow-hidden ring-1 ring-white/10 shrink-0">
              <img src="/logo.png" alt="" className="w-full h-full object-cover" width={40} height={40} />
            </div>
            <span className="font-bold text-lg text-white">TradeAnalyzer Pro</span>
          </div>
          <div className="hidden sm:flex items-center gap-6">
            <a href="#l-ai" className="text-slate-400 hover:text-white text-sm font-medium transition-colors">L'AI</a>
            <a href="#come-funziona" className="text-slate-400 hover:text-white text-sm font-medium transition-colors">Come funziona</a>
            <a href="#prezzi" className="text-slate-400 hover:text-white text-sm font-medium transition-colors">Prezzi</a>
            <button onClick={() => setShowAuth(true)} className="text-slate-400 hover:text-white text-sm font-medium">
              Accedi
            </button>
            {APP_STORE_READY ? (
              <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm bg-gradient-to-r from-violet-500 to-cyan-500 text-white hover:opacity-90 transition-opacity">
                <AppleLogo className="w-5 h-5" /> Scarica su App Store
              </a>
            ) : (
              <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm bg-gradient-to-r from-amber-500/20 to-yellow-500/20 text-amber-200 cursor-default border border-amber-400/50">
                <AppleLogo className="w-5 h-5 text-amber-200" /> Presto su App Store
              </span>
            )}
          </div>
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="sm:hidden p-2 text-slate-400">
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        {mobileMenuOpen && (
          <div className="sm:hidden bg-slate-900 border-t border-slate-800 px-4 py-4 space-y-3">
            <a href="#l-ai" className="block text-slate-300 py-2" onClick={() => setMobileMenuOpen(false)}>L'AI</a>
            <a href="#come-funziona" className="block text-slate-300 py-2" onClick={() => setMobileMenuOpen(false)}>Come funziona</a>
            <a href="#prezzi" className="block text-slate-300 py-2" onClick={() => setMobileMenuOpen(false)}>Prezzi</a>
            <button onClick={() => { setShowAuth(true); setMobileMenuOpen(false); }} className="block w-full text-left py-2 text-slate-400">Accedi</button>
            {APP_STORE_READY ? (
              <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 w-full py-3 rounded-xl font-semibold bg-gradient-to-r from-violet-500 to-cyan-500 text-white">
                <AppleLogo className="w-5 h-5" /> Scarica su App Store
              </a>
            ) : (
              <span className="flex items-center justify-center gap-2 w-full py-3 rounded-xl font-semibold bg-gradient-to-r from-amber-500/20 to-yellow-500/20 text-amber-200 border border-amber-400/50">
                <AppleLogo className="w-5 h-5 text-amber-200" /> Presto su App Store
              </span>
            )}
          </div>
        )}
      </header>

      {/* Hero */}
      <section className="relative pt-28 sm:pt-36 pb-16 sm:pb-24 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold leading-[1.15] mb-6">
            Anticipa il Mercato con l'<span className="bg-gradient-to-r from-violet-400 via-fuchsia-400 to-cyan-400 bg-clip-text text-transparent">Intelligenza Artificiale</span>.
          </h1>
          <p className="text-lg sm:text-xl text-slate-400 max-w-2xl mx-auto mb-8">
            TradeAnalyzer analizza oltre 50 asset e scannerizza il mercato ogni 15 minuti per individuare automaticamente i setup di trading con la più alta probabilità.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
            {APP_STORE_READY ? (
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group w-full sm:w-auto inline-flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-violet-500 to-cyan-500 text-white rounded-xl font-bold text-lg hover:shadow-lg hover:shadow-violet-500/30 hover:scale-[1.02] transition-all"
              >
                <AppleLogo className="w-7 h-7" />
                Scarica su App Store
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
            ) : (
              <span className="w-full sm:w-auto inline-flex items-center justify-center gap-3 px-8 py-4 rounded-xl font-bold text-lg bg-gradient-to-r from-amber-500/25 to-yellow-500/25 text-amber-200 border border-amber-400/50 cursor-default">
                <AppleLogo className="w-7 h-7 text-amber-200" />
                Presto su App Store
              </span>
            )}
            <button
              onClick={() => setShowAuth(true)}
              className="w-full sm:w-auto px-8 py-4 rounded-xl font-semibold text-lg border border-slate-600 text-slate-300 hover:border-slate-500 hover:text-white transition-colors"
            >
              Usa da browser
            </button>
          </div>
          {/* Key metrics */}
          <div className="max-w-4xl mx-auto grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
            <div className="rounded-xl border border-slate-700/70 bg-slate-900/70 px-4 py-3 text-center">
              <p className="text-xl sm:text-2xl font-black text-white leading-none">50+</p>
              <p className="mt-1 text-[11px] uppercase tracking-wider text-slate-400">Simboli</p>
            </div>
            <div className="rounded-xl border border-slate-700/70 bg-slate-900/70 px-4 py-3 text-center">
              <p className="text-xl sm:text-2xl font-black text-cyan-300 leading-none">15 min</p>
              <p className="mt-1 text-[11px] uppercase tracking-wider text-slate-400">Ciclo scan</p>
            </div>
            <div className="rounded-xl border border-slate-700/70 bg-slate-900/70 px-4 py-3 text-center">
              <p className="text-xl sm:text-2xl font-black text-emerald-300 leading-none">175%</p>
              <p className="mt-1 text-[11px] uppercase tracking-wider text-slate-400">Score max</p>
            </div>
            <div className="rounded-xl border border-slate-700/70 bg-slate-900/70 px-4 py-3 text-center">
              <p className="text-xl sm:text-2xl font-black text-violet-300 leading-none">4</p>
              <p className="mt-1 text-[11px] uppercase tracking-wider text-slate-400">Strategie</p>
            </div>
            <div className="col-span-2 sm:col-span-3 lg:col-span-1 rounded-xl border border-violet-500/40 bg-violet-500/10 px-4 py-3 text-center">
              <p className="text-base sm:text-lg font-black text-violet-300 leading-none">AI on-demand</p>
              <p className="mt-1 text-[11px] uppercase tracking-wider text-violet-200/80">Analisi avanzata</p>
            </div>
          </div>
        </div>
      </section>

      {/* L'AI analizza il mercato */}
      <section id="l-ai" className="relative py-20 sm:py-28 px-4 sm:px-6 border-t border-slate-800/80">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-center mb-6 leading-tight">
            L'AI analizza il mercato per te.
          </h2>
          <p className="text-xl text-slate-400 text-center leading-relaxed mb-10">
            Non passi ore sui grafici. Il sistema scansiona decine di simboli, valuta trend e confluence su più timeframe e ti indica quali setup hanno maggiore probabilità di muoversi in una direzione piuttosto che in un'altra.
          </p>
          <p className="text-lg text-slate-500 text-center leading-relaxed mb-12">
            Vedi la leaderboard. Scegli il setup. Richiedi l'analisi approfondita. L'AI ti restituisce una lettura chiara: dove il mercato potrebbe andare, dove entrare, dove mettere stop e take profit. Tu decidi con più informazioni.
          </p>
          <div className="flex justify-center">
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-slate-800/80 border border-slate-700">
              <Brain className="w-5 h-5 text-violet-400" />
              <span className="text-slate-300 font-medium">Analisi on-demand sui setup qualificati</span>
            </div>
          </div>
        </div>
      </section>

      {/* Perché */}
      <section id="perche" className="relative py-20 sm:py-28 px-4 sm:px-6 bg-slate-900/20">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4">Perché TradeAnalyzer Pro</h2>
          <p className="text-slate-400 text-center max-w-xl mx-auto mb-14">
            Dati real-time, analisi AI e calcolatore di rischio integrati. Piattaforma multicanale per uso desktop e mobile.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyApp.map((item, i) => (
              <div key={i} className="p-6 rounded-2xl bg-slate-900/60 border border-slate-800 hover:border-slate-600 transition-all">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center mb-4`}>
                  <item.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Come funziona */}
      <section id="come-funziona" className="relative py-20 sm:py-28 px-4 sm:px-6 border-t border-slate-800/80">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4">Come funziona</h2>
          <p className="text-slate-400 text-center mb-14 max-w-2xl mx-auto text-lg">
            In tre passi: lo scanner fa il lavoro, tu scegli tra i setup migliori e decidi come operare con l'aiuto dell'AI.
          </p>
          <div className="space-y-6">
            {howItWorksBlocks.map((step, i) => (
              <div key={i} className="flex gap-6 p-6 sm:p-8 rounded-2xl bg-slate-900/60 border border-slate-800">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center text-lg font-bold tabular-nums">
                  {step.n}
                </div>
                <p className="text-slate-300 text-base sm:text-lg leading-relaxed pt-1">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Assistente */}
      <section className="relative py-14 sm:py-18 px-4 sm:px-6 border-t border-slate-800/80">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-2xl border border-violet-500/30 bg-gradient-to-r from-violet-500/10 via-slate-900/70 to-cyan-500/10 p-6 sm:p-8">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl bg-violet-500/20 border border-violet-400/30 flex items-center justify-center flex-shrink-0">
                <Brain className="w-6 h-6 text-violet-300" />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-wider text-violet-300/90 font-semibold mb-1">Nuovo Assistente</p>
                <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">Un piano su misura, non un numero casuale</h3>
                <p className="text-slate-300 text-sm sm:text-base leading-relaxed mb-4">
                  Imposti capitale, obiettivo e frequenza operativa. L&apos;Assistente ti propone un piano realistico con rischio suggerito,
                  trade stimati e progressione del capitale nel tempo.
                </p>
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="px-3 py-1.5 rounded-lg bg-slate-800/80 border border-slate-700 text-slate-300">Profili rischio</span>
                  <span className="px-3 py-1.5 rounded-lg bg-slate-800/80 border border-slate-700 text-slate-300">Data scadenza obiettivo</span>
                  <span className="px-3 py-1.5 rounded-lg bg-slate-800/80 border border-slate-700 text-slate-300">Tracking progressi</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Prezzi */}
      <section id="prezzi" className="relative py-20 sm:py-28 px-4 sm:px-6 border-t border-slate-800/80">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4">Scegli il piano giusto per te</h2>
          <p className="text-slate-400 text-center max-w-2xl mx-auto mb-10">
            Due piani, stesso motore AI. Cambia la profondità del servizio, non la qualità.
          </p>

          <div className="flex flex-wrap justify-center gap-2 mb-10">
            {billingOptions.map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => setBillingCycle(opt.key)}
                className={`px-4 py-2 rounded-xl text-sm font-semibold border transition-all ${
                  billingCycle === opt.key
                    ? 'bg-violet-500/20 border-violet-400/50 text-violet-200'
                    : 'bg-slate-900/60 border-slate-700 text-slate-300 hover:border-slate-500'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-7">
              <p className="text-xs uppercase tracking-wider text-slate-300 font-semibold mb-2">Free</p>
              <h3 className="text-3xl font-black text-white mb-1">€0</h3>
              <p className="text-sm text-slate-400 mb-6">/mese</p>
              <ul className="space-y-2 text-sm text-slate-300 mb-7">
                <li>• 3 analisi AI per valuta al giorno</li>
                <li>• Scanner multi-asset ogni 15 minuti</li>
                <li>• Leaderboard setup in tempo reale</li>
                <li>• Checklist illimitata per ogni valuta</li>
                <li>• Calcolatore lot size e rischio</li>
                <li>• Assistente non incluso</li>
              </ul>
              <button onClick={() => setShowAuth(true)} className="w-full py-3 rounded-xl border border-slate-600 text-slate-200 font-semibold hover:border-slate-400 hover:text-white transition-colors">
                Inizia Gratis
              </button>
            </div>

            <div className="rounded-2xl border border-slate-700 bg-slate-900/60 p-7">
              <p className="text-xs uppercase tracking-wider text-cyan-300 font-semibold mb-2">Pro</p>
              <h3 className="text-3xl font-black text-white mb-1">€{activeBilling.pro}</h3>
              <p className="text-sm text-slate-400 mb-6">{activeBilling.suffix}</p>
              {activeBilling.proStrike && (
                <p className="text-xs text-slate-500 mb-4">Invece di <span className="line-through">€{activeBilling.proStrike}</span></p>
              )}
              <ul className="space-y-2 text-sm text-slate-300 mb-7">
                <li>• 20 analisi AI giornaliere</li>
                <li>• Scanner 24/7 su 50+ simboli (forex, oro, indici)</li>
                <li>• Leaderboard AI per valuta con success rate</li>
                <li>• AI Deep Analysis: bias, zone FVG/AOI, entry/SL/TP</li>
                <li>• Conferma reazione (engulfing) + contesto risk day</li>
                <li>• Statistiche zone: touch rate + retracement pips</li>
                <li>• Statistiche qualità setup per fasce di confluenza</li>
                <li>• Checklist illimitata per ogni valuta</li>
                <li>• Calcolatore completo (lot size, R:R, gestione rischio)</li>
                <li>• 1 obiettivo Assistente alla volta</li>
                <li>• Trade Log Assistente con tracking progressione capitale</li>
                <li>• Alert e monitoraggio setup in tempo reale</li>
              </ul>
              <a
                href={stripeProUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-3 rounded-xl border border-slate-600 text-slate-200 font-semibold hover:border-slate-400 hover:text-white transition-colors inline-flex items-center justify-center"
              >
                Attiva Pro
              </a>
            </div>

            <div className="rounded-2xl border border-violet-500/40 bg-gradient-to-b from-violet-500/15 to-slate-900/70 p-7 relative overflow-hidden">
              <div className="absolute top-0 right-0 px-3 py-1 text-[11px] font-semibold rounded-bl-lg bg-violet-500 text-white">Premium</div>
              <p className="text-xs uppercase tracking-wider text-violet-300 font-semibold mb-2">Gold / Premium</p>
              <h3 className="text-3xl font-black text-white mb-1">€{activeBilling.premium}</h3>
              <p className="text-sm text-slate-300 mb-2">{activeBilling.suffix}</p>
              {activeBilling.premiumStrike && (
                <p className="text-xs text-violet-200/80 mb-4">Invece di <span className="line-through">€{activeBilling.premiumStrike}</span></p>
              )}
              <p className="inline-flex items-center px-2.5 py-1 rounded-lg text-[11px] font-semibold text-violet-200 bg-violet-500/20 border border-violet-400/30 mb-5">
                {activeBilling.badge || 'Piano completo'}
              </p>
              <ul className="space-y-2 text-sm text-slate-200 mb-7">
                <li>• Tutto del piano Pro</li>
                <li>• Analisi AI illimitate</li>
                <li>• 3 obiettivi Assistente</li>
                <li>• Priorità elaborazione analisi</li>
                <li>• Accesso completo a tutte le funzioni premium</li>
                <li>• Priorità supporto</li>
              </ul>
              <a
                href={stripePremiumUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-violet-500 to-cyan-500 hover:opacity-90 transition-opacity inline-flex items-center justify-center"
              >
                Attiva Premium
              </a>
            </div>
          </div>

          <div className="mt-8 rounded-2xl border border-emerald-500/40 bg-emerald-500/10 p-5 sm:p-6">
            <p className="text-[11px] uppercase tracking-wider text-emerald-300 font-semibold mb-1">Offerta Partner Broker</p>
            <p className="text-white font-semibold text-lg">Premium 12 mesi incluso (valore €599)</p>
            <p className="text-sm text-emerald-100/85 mt-1">Apri un conto con il nostro broker partner e sblocca 1 anno di Premium gratuitamente (in base ai requisiti promozionali).</p>
          </div>
        </div>
      </section>

      {/* Social proof + CTA */}
      <section className="relative py-20 sm:py-28 px-4 sm:px-6 bg-slate-900/20">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-12">
            <div className="rounded-2xl bg-slate-900/70 border border-slate-700 p-6 text-center hover:border-slate-600 transition-colors">
              <span className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-violet-400 to-cyan-400 bg-clip-text text-transparent tabular-nums">1.200+</span>
              <span className="block text-slate-400 text-sm font-medium mt-1">trader attivi</span>
            </div>
            <div className="rounded-2xl bg-slate-900/70 border border-slate-700 p-6 text-center hover:border-slate-600 transition-colors">
              <span className="text-3xl sm:text-4xl font-bold text-amber-400 tabular-nums">4.9</span>
              <span className="block text-slate-400 text-sm font-medium mt-1">/5 rating</span>
            </div>
            <div className="rounded-2xl bg-slate-900/70 border border-slate-700 p-6 text-center hover:border-slate-600 transition-colors">
              <span className="text-3xl sm:text-4xl font-bold text-cyan-400 tabular-nums">50+</span>
              <span className="block text-slate-400 text-sm font-medium mt-1">asset analizzati</span>
            </div>
            <div className="rounded-2xl bg-slate-900/70 border border-slate-700 p-6 text-center hover:border-slate-600 transition-colors col-span-2 lg:col-span-1">
              <span className="text-3xl sm:text-4xl font-bold text-emerald-400 tabular-nums">15 min</span>
              <span className="block text-slate-400 text-sm font-medium mt-1">ciclo scan</span>
            </div>
          </div>
          <blockquote className="text-slate-300 text-lg sm:text-xl text-center max-w-2xl mx-auto mb-12 italic">
            "Lo scanner mi fa risparmiare almeno 4 ore al giorno. Le analisi AI sono incredibilmente accurate."
          </blockquote>
          <p className="text-slate-500 text-sm text-center mb-14">— Marco T., trader</p>

          <div className="text-center">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3">Scarica l'app</h2>
            <p className="text-slate-400 mb-8 max-w-lg mx-auto">
              Stessa piattaforma su iPhone. Leaderboard, analisi AI e alert in tempo reale.
            </p>
            {APP_STORE_READY ? (
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center justify-center gap-3 px-10 py-4 bg-gradient-to-r from-violet-500 to-cyan-500 text-white rounded-xl font-bold text-lg hover:shadow-lg hover:shadow-violet-500/30 hover:scale-[1.02] transition-all"
              >
                <AppleLogo className="w-7 h-7" />
                Scarica su App Store
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
            ) : (
              <span className="inline-flex items-center justify-center gap-3 px-10 py-4 rounded-xl font-bold text-lg bg-gradient-to-r from-amber-500/25 to-yellow-500/25 text-amber-200 border border-amber-400/50 cursor-default">
                <AppleLogo className="w-7 h-7 text-amber-200" />
                Presto su App Store
              </span>
            )}
            <p className="mt-6 text-slate-500 text-sm">
              Preferisci il browser? <button onClick={() => setShowAuth(true)} className="text-violet-400 hover:text-violet-300 font-medium">Accedi alla piattaforma</button>
            </p>
          </div>
        </div>
      </section>

      <footer className="relative border-t border-slate-800 py-10 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg overflow-hidden ring-1 ring-white/10 shrink-0">
              <img src="/logo.png" alt="" className="w-full h-full object-cover" width={32} height={32} />
            </div>
            <span className="font-bold text-slate-300">TradeAnalyzer Pro</span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-slate-500">
            <a href="/privacy-policy.html" className="hover:text-slate-300">Privacy</a>
            <button onClick={() => setShowAuth(true)} className="hover:text-slate-300">Accedi</button>
            <a href="#prezzi" className="hover:text-slate-300">Prezzi</a>
            {APP_STORE_READY && (
              <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="hover:text-slate-300">App Store</a>
            )}
          </div>
        </div>
        <p className="max-w-3xl mx-auto mt-8 text-center text-slate-600 text-xs leading-relaxed">
          TradeAnalyzer Pro non gestisce capitali e non fornisce consulenza finanziaria. Le percentuali sono somme di confluence, non probabilità di vittoria. Il trading comporta rischi.
        </p>
      </footer>
    </div>
  );
}
